import React, { useCallback, useMemo } from 'react';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { getRtkErrorMessage } from 'api/getRtkErrorMessage';
import { type TColumns, useSortColumnsModal } from 'hooks/useSortColumnsModal';
import { useTableTitle } from 'hooks/useTableTitle';
import intl, { getText } from 'i18n/intl';
import {
  UploadObjectsResult,
  useQueueImportJobMutation,
  useUploadObjectsFileMutation,
} from 'pages/objectManager/components/import/api/importObjectsApi';
import { errorNotification } from 'utils/notifications';
import { stillSelectedDetail } from 'utils/uploadMessages';
import {
  Alert,
  Button,
  EditableTable,
  Icon,
  Select,
} from '@timeedit/ui-components';
import {
  DraggerProps,
  Flex,
  Modal,
  Steps,
  Upload,
  UploadFile,
  theme,
} from '@timeedit/ui-components/antd';
import { LabelWrapper } from '@timeedit/ui-components/components/InputComponent/Input';
import './ImportObjectModal.scss';

type Props = {
  objectTypeId: string | number;
  open: boolean;
  onClose: () => void;
};

const language: any = intl.messages;

/** Mirrors the `fileSize` limit multer enforces on the import endpoint. */
const MAX_FILE_SIZE_MB = 200;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1000 * 1000;

const ACCEPTED_FILE_TYPES = [
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

type RtkError = FetchBaseQueryError | SerializedError;

type UploadError = { message: string; details: string[] };

type CompletedUpload = { results: UploadObjectsResult; filename: string };

type ImportOptions = {
  separator: string;
  multiOperationType: 'add' | 'replace' | undefined;
};

const INITIAL_OPTIONS: ImportOptions = {
  separator: '|',
  multiOperationType: undefined,
};

/** The response body te-object-service sends for a rejected upload. */
type UploadErrorBody = { errors?: { message?: string }[] };

/** One entry per offending column, when the spreadsheet header is at fault. */
const getUploadErrorDetails = (error: RtkError): string[] => {
  if (!('data' in error)) return [];

  const { errors } = (error.data ?? {}) as UploadErrorBody;
  if (!Array.isArray(errors)) return [];

  return errors
    .map((entry) => entry?.message)
    .filter((message): message is string => typeof message === 'string');
};

const getUploadError = (error: RtkError): UploadError => ({
  message: getRtkErrorMessage(error),
  details: getUploadErrorDetails(error),
});

const ImportObjectModal: React.FC<Props> = ({
  open,
  onClose: onParentClose,
  objectTypeId,
}) => {
  const { token } = theme.useToken();

  const [uploadFile] = useUploadObjectsFileMutation();
  const [queueImportJob] = useQueueImportJobMutation();

  const [completedUpload, setCompletedUpload] =
    React.useState<CompletedUpload | null>(null);

  /** Live view of `completedUpload` for the async queue request. */
  const completedUploadRef = React.useRef<CompletedUpload | null>(null);

  const applyCompletedUpload = useCallback((next: CompletedUpload | null) => {
    completedUploadRef.current = next;
    setCompletedUpload(next);
  }, []);
  const [uploadError, setUploadError] = React.useState<UploadError | null>(
    null
  );

  const [fileList, setFileList] = React.useState<UploadFile[]>([]);
  const fileListRef = React.useRef<UploadFile[]>([]);
  const applyFileList = useCallback((nextFileList: UploadFile[]) => {
    fileListRef.current = nextFileList;
    setFileList(nextFileList);
  }, []);
  const [options, setOptions] = React.useState<ImportOptions>(INITIAL_OPTIONS);

  const [step, setStep] = React.useState(0);
  const steps = useMemo(
    () => [
      {
        title: 'Upload file',
        okButtonDisabled: !completedUpload,
        okButtonText: language.next,
        cancelButtonText: language.cancel,
      },
      {
        title: 'Import settings',
        okButtonDisabled: !completedUpload || !options.multiOperationType,
        okButtonText: language.validate_file,
        cancelButtonText: language.cancel,
      },
    ],
    [
      completedUpload,
      options.multiOperationType,
      language.validate_file,
      language.cancel,
      language.import_settings,
    ]
  );

  const onClose = useCallback(() => {
    setStep(0);
    applyCompletedUpload(null);
    setUploadError(null);
    applyFileList([]);
    setOptions(INITIAL_OPTIONS);
    onParentClose();
  }, [onParentClose, applyFileList, applyCompletedUpload]);

  const onFileListChange = useCallback(
    (nextFileList: UploadFile[]) => {
      applyFileList(nextFileList);

      if (nextFileList.length === 0) {
        applyCompletedUpload(null);
        setUploadError(null);
      }
    },
    [applyFileList, applyCompletedUpload]
  );

  const handleNextStep = useCallback(async () => {
    if (step < steps.length - 1) {
      setStep((prev) => prev + 1);
    } else {
      if (!completedUpload || !options.multiOperationType) return;

      const queued = completedUpload;
      const result = await queueImportJob({
        jobId: queued.results.id,
        separator: options.separator,
        multiOperationType: options.multiOperationType,
      });

      if (result.error) {
        errorNotification('Failed to queue import job', [
          getRtkErrorMessage(result.error),
        ]);
        return;
      }

      if (completedUploadRef.current === queued) {
        onClose();
      }
    }
  }, [
    steps.length,
    step,
    completedUpload,
    options.separator,
    options.multiOperationType,
    queueImportJob,
    onClose,
  ]);

  const onUpload: DraggerProps['customRequest'] = useCallback(
    async (options) => {
      const data = new FormData();

      const { file, onError, onSuccess } = options;

      data.append('file', file);
      data.append('objectType', objectTypeId.toString());

      applyCompletedUpload(null);

      const response = await uploadFile(data);

      const uid = typeof file !== 'string' && 'uid' in file ? file.uid : null;
      if (uid == null || !fileListRef.current.some((f) => f.uid === uid)) {
        return;
      }

      if (response.error) {
        const error = getUploadError(response.error);

        setUploadError(error);
        onError?.(new Error(error.message), response.error);
        return;
      }

      setUploadError(null);
      onSuccess?.(response.data);
      setOptions((prev) => ({
        ...prev,
        separator: response.data?.detectedSeparator ?? '|',
      }));

      if (response.data) {
        applyCompletedUpload({
          results: response.data,
          filename: typeof file !== 'string' && 'name' in file ? file.name : '',
        });
      }
    },
    [objectTypeId, uploadFile, applyCompletedUpload]
  );

  const currentStep = steps[step];

  const footer = useMemo(() => {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <Button onClick={onClose}>{currentStep.cancelButtonText}</Button>
        <Flex gap={8}>
          {step > 0 && (
            <Button onClick={() => setStep((prev) => prev - 1)}>
              {language.back}
            </Button>
          )}
          <Button
            type="primary"
            onClick={handleNextStep}
            disabled={currentStep.okButtonDisabled}
          >
            {currentStep.okButtonText}
          </Button>
        </Flex>
      </div>
    );
  }, [currentStep, onClose, handleNextStep, step, language.back]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={language.import_objects}
      style={{ minWidth: '60vw' }}
      footer={footer}
    >
      <Flex
        vertical
        gap={token.margin}
        className="import-object-modal-container"
      >
        <Steps current={step} onChange={setStep} items={steps} />
        {step === 0 && (
          <FileSelection
            onUpload={onUpload}
            uploadError={uploadError}
            fileList={fileList}
            onFileListChange={onFileListChange}
          />
        )}
        {step === 1 && completedUpload && (
          <ImportOverview
            fileUploadResults={completedUpload.results}
            filename={completedUpload.filename}
            objectType={objectTypeId.toString()}
            options={options}
            setOptions={setOptions}
          />
        )}
      </Flex>
    </Modal>
  );
};

const FileSelection = ({
  onUpload,
  uploadError,
  fileList,
  onFileListChange,
}: {
  onUpload: DraggerProps['customRequest'];
  uploadError: UploadError | null;
  fileList: UploadFile[];
  onFileListChange: (fileList: UploadFile[]) => void;
}) => {
  // Only a failed upload is re-laid-out. A successful one keeps antd's own
  // list item untouched.
  const itemRender = useCallback<NonNullable<DraggerProps['itemRender']>>(
    (originNode, file) => {
      if (file.status !== 'error' || !uploadError) return originNode;

      return (
        <div className="import-upload-error">
          <Icon
            id="FileExcelOutlined"
            alt=""
            className="import-upload-error-icon"
          />
          <div className="import-upload-error-body">
            {originNode}
            <div className="import-upload-error-message">
              <Icon id="CloseCircleOutlined" alt="" />
              <span>{uploadError.message}</span>
            </div>
            {uploadError.details.length > 0 && (
              <ul className="import-upload-error-details">
                {uploadError.details.map((detail, index) => (
                  <li key={`${index}-${detail}`}>{detail}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      );
    },
    [uploadError]
  );

  const beforeUpload = useCallback<NonNullable<DraggerProps['beforeUpload']>>(
    (file) => {
      if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
        errorNotification(
          'Invalid file type. Please upload a CSV or Excel file.',
          stillSelectedDetail(fileList)
        );
        return Upload.LIST_IGNORE;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        errorNotification(
          `Max file size is ${MAX_FILE_SIZE_MB} MB`,
          stillSelectedDetail(fileList)
        );
        return Upload.LIST_IGNORE;
      }

      return true;
    },
    [fileList]
  );

  return (
    <div>
      <Upload.Dragger
        multiple={false}
        maxCount={1}
        fileList={fileList}
        onChange={(info) => onFileListChange(info.fileList)}
        customRequest={onUpload}
        accept=".csv,.xls,.xlsx"
        itemRender={itemRender}
        beforeUpload={beforeUpload}
      >
        <p className="ant-upload-drag-icon">
          <Icon id="InboxOutlined" alt="File icon" />
        </p>
        <p className="ant-upload-text">{language.click_or_drag_file}</p>
        <p className="ant-upload-hint">{language.support_single_file_upload}</p>
      </Upload.Dragger>
    </div>
  );
};

const SEPARATOR_OPTIONS = [
  {
    label: getText('object_manager.export-modal.separator.comma'),
    value: ',',
  },
  {
    label: getText('object_manager.export-modal.separator.semicolon'),
    value: ';',
  },
  {
    label: getText('object_manager.export-modal.separator.pipe'),
    value: '|',
  },
  {
    label: getText('object_manager.export-modal.separator.forward-slash'),
    value: '/',
  },
];

const DEFAULT_SEPARATOR = '|';

const OPERATION_TYPE_OPTIONS: {
  label: any;
  value: NonNullable<ImportOptions['multiOperationType']>;
}[] = [
  {
    value: 'add',
    label: 'Add',
  },
  { value: 'replace', label: 'Replace' },
];

const OPERATION_TYPE_HINTS: Record<
  NonNullable<ImportOptions['multiOperationType']>,
  string
> = {
  add: 'Adds values to multi-value fields, overwrites single-value fields.',
  replace:
    "Overwrites both single- and multi-value fields with the file's data.",
};

/**
 * Prefixed so the keys stay non-numeric: `useSortColumnsModal` orders columns by
 * the global field order, which is keyed on numeric field ids.
 */
const columnKey = (index: number) => `column-${index}`;

type PreviewRow = Record<string, string>;

const ImportOverview: React.FC<{
  fileUploadResults: UploadObjectsResult;
  filename: string;
  objectType: string;
  options: ImportOptions;
  setOptions: (options: ImportOptions) => void;
}> = ({ fileUploadResults, filename, options, setOptions }) => {
  const { token } = theme.useToken();

  const rowData = useMemo(
    () =>
      fileUploadResults.exampleRows.map((row, rowIndex) => {
        const rowData: PreviewRow = row.reduce((acc, cell, cellIndex) => {
          acc[columnKey(cellIndex)] = cell;

          return acc;
        }, {} as PreviewRow);
        rowData.key = rowIndex.toString();
        return rowData;
      }),
    [fileUploadResults]
  );

  const columnSettings = useMemo<TColumns<string, PreviewRow>>(
    () =>
      Object.fromEntries(
        fileUploadResults.foundColumns.map((foundColumn, index) => [
          columnKey(index),
          {
            key: columnKey(index),
            label:
              foundColumn.attribute ??
              foundColumn.field ??
              (foundColumn.availabilityRelation
                ? `availability:${foundColumn.availabilityRelation}`
                : foundColumn.cell),
            resizable: true,
            ellipsis: true,
          },
        ])
      ),
    [fileUploadResults]
  );

  const { sortColumnsModal, openSortColumnsModal, sortedColumns } =
    useSortColumnsModal<string, PreviewRow>(columnSettings);

  const titleActions = useMemo(
    () => ({
      right: (
        <Button onClick={openSortColumnsModal} size="small">
          {getText('ui.table.edit_columns')}
        </Button>
      ),
    }),
    [openSortColumnsModal]
  );

  const title = useTableTitle<PreviewRow>({
    total: 0,
    showTotalNumber: false,
    actions: titleActions,
  });

  return (
    <Flex
      gap={token.margin}
      vertical
      style={{
        overflowX: 'auto',
      }}
    >
      <LabelWrapper label="File name">{filename}</LabelWrapper>
      <LabelWrapper label="Operation type">
        <Flex gap={12} align="center" className="import-operation-type">
          <Select
            value={options.multiOperationType}
            placeholder="Select operation type"
            options={OPERATION_TYPE_OPTIONS}
            onChange={(value) =>
              setOptions({
                separator: options.separator ?? DEFAULT_SEPARATOR,
                multiOperationType: value,
              })
            }
            style={{ width: '240px', flexShrink: 0 }}
          />
          {options.multiOperationType && (
            <Alert
              className="import-objects-alert "
              showIcon
              type="info"
              description={OPERATION_TYPE_HINTS[options.multiOperationType]}
            />
          )}
        </Flex>
      </LabelWrapper>
      <LabelWrapper
        label="Separator"
        tooltip={getText('object_manager.export-modal.separator.tooltip')}
      >
        <Select
          value={options.separator}
          onChange={(value) =>
            setOptions({
              separator: value,
              multiOperationType: options.multiOperationType,
            })
          }
          options={SEPARATOR_OPTIONS}
          style={{ width: '240px' }}
        />
      </LabelWrapper>

      {fileUploadResults.warnings.length > 0 && (
        <Alert
          showIcon
          type="warning"
          className="import-objects-alert "
          description={
            <>
              <span>The following warnings were found in the file:</span>
              <ul>
                {fileUploadResults.warnings.map((warning, index) => {
                  const message = warning.cell
                    ? `${warning.cell}: ${warning.message}`
                    : warning.message;
                  return (
                    <li
                      key={index}
                      style={{ listStyleType: 'disc', marginLeft: '1em' }}
                    >
                      {message}
                    </li>
                  );
                })}
              </ul>
            </>
          }
        />
      )}

      {fileUploadResults.exampleRows.length > 0 && (
        <>
          <Alert
            className="import-objects-alert "
            type="info"
            showIcon
            description={getText('preview_import_file_info')}
          />
          <div className="import-objects-preview">
            <EditableTable<PreviewRow>
              title={title}
              sticky={{
                offsetHeader: 39 /* height of title with "edit columns" */,
              }}
              columns={sortedColumns}
              rowKey="key"
              dataSource={rowData}
              pagination={false}
              scroll={{
                x: 'max-content',
              }}
            />
            {sortColumnsModal}
          </div>
        </>
      )}
    </Flex>
  );
};

export default ImportObjectModal;
